import { Injectable } from '@angular/core';
import { AngularFireRemoteConfig } from '@angular/fire/compat/remote-config';
import { FeatureToggleCode } from 'app/components/models/enum/feature-toggle-code.enum';
import { FeatureToggle } from 'app/components/models/feature-toggle.model';
import { BehaviorSubject, catchError, from, map, of, switchMap, tap } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class FeatureToggleService {
  features: Array<FeatureToggle> = null;
  features$ = new BehaviorSubject([]);
  private username = null;
  defaults = [];

  constructor(private readonly remoteConfig: AngularFireRemoteConfig) {
    this.init();
  }

  private async init(): Promise<void> {
    await this.remoteConfig.ensureInitialized();
    await this.remoteConfig.activate();

    const data = await this.remoteConfig.defaultConfig;
    if (data) {
      this.defaults = Object.entries(data).map(([name, value]) => ({
        id: name,
        name,
        value,
      }));
    }
  }

  getFeaturesByUsername(username = '', renewFeatures = true) {
    if (username === this.username && this.features && !renewFeatures) {
      return of(this.features);
    }

    this.username = username;
    return this.getFireBaseFeatures(username).pipe(
      map((firebaseFeatures) => this.mergeFeatures(firebaseFeatures)),
      tap((features) => (this.features = features)),
    );
  }

  private getFireBaseFeatures(username: string) {
    return from(this.remoteConfig.fetchAndActivate()).pipe(
      switchMap(() => from(this.remoteConfig.getAll())),
      map((all) => FeatureToggle.featureObjectToParameters(all)),
      map((params) =>
        params
          .map(FeatureToggle.fromParameter)
          .filter((toggle) =>
            username ? toggle.enabledForUsername(username) : true
          )
      ),
      catchError(() => of(this.features ?? this.defaults))
    );
  }

  private mergeFeatures(features: FeatureToggle[]) {
    const unique = Array.from(
      features.reduce((map, ft) => map.set(ft.id, ft), new Map()).values()
    );
    this.features$.next(unique);
    return unique;
  }

  hasFeatureEnabled(featureId: FeatureToggleCode) {
    return this.features?.some(
      (feature) =>
        feature.id === featureId &&
        (feature.enabledForUsername(this.username) || feature.value === true),
    );
  }
}